import { useEffect } from "react";
import { useIdleTimer } from "react-idle-timer";
import {
  RootState,
  useAppDispatch,
  useAppSelector
} from "common/redux/core/root.reducer";
import { userActions } from "common/redux/user";
import { ENV_CONFIG } from "common/config/env.config";
import { CrossTabMessage, LogoutType } from "common/enums/logout-type.enum";
import { AppStorageKey } from "common/models";

const HARD_SESSION_TIMEOUT_MS = ENV_CONFIG.USER_HARD_SESSION_TIMEOUT * 1000;

const useSessionTimeout = () => {
  const dispatch = useAppDispatch();
  const IDLE_TIME_OUT =
    1000 * 60 * parseInt(ENV_CONFIG.USER_IDLE_WARNING_TIMEOUT?.toString());

  const IDLE_AUTO_LOGOUT_MS =
    1000 * 60 * parseInt(ENV_CONFIG.USER_IDLE_AUTO_LOGOUT_TIMEOUT?.toString());

  const TOTAL_IDLE_WINDOW_MS = IDLE_TIME_OUT + IDLE_AUTO_LOGOUT_MS;

  const showTimerModal = useAppSelector(
    (state: RootState) => state.user.showSessionTimeoutTimer
  );

  const hasUserLoggedOut = useAppSelector(
    (state: RootState) => state.user.userLoggedOut
  );

  const handleOnIdle = () => {
    // Suppress idle timeout modal if hard session will end before the
    // idle logout would fire. Hard session warnings handle the UX in that case.
    if (getHardSessionRemainingMs() <= TOTAL_IDLE_WINDOW_MS) {
      return;
    }
    dispatch(userActions.updateSessionTimeoutModalVisibility(true));
  };

  const getHardSessionRemainingMs = (): number => {
    const loginTimestamp = localStorage.getItem(AppStorageKey.LOGIN_TIMESTAMP);
    if (!loginTimestamp) return Infinity;
    const parsed = parseInt(loginTimestamp, 10);
    if (isNaN(parsed)) return Infinity;
    const elapsed = Date.now() - parsed;
    return Math.max(0, HARD_SESSION_TIMEOUT_MS - elapsed);
  };

  const handleOnMessage = (data: string) => {
    if (data === CrossTabMessage.LOGOUT) {
      dispatch(userActions.hasUserLoggedOut(true));
      dispatch(userActions.userLogoutType(LogoutType.SESSION_TIMEOUT));
    } else if (data === CrossTabMessage.CONTINUE) {
      // Close modal and reset timer in all tabs when continue is clicked in any tab
      dispatch(userActions.updateSessionTimeoutModalVisibility(false));
      dispatch(userActions.hasUserLoggedOut(false));
    }
  };

  const { reset, message } = useIdleTimer({
    timeout: IDLE_TIME_OUT,
    onIdle: handleOnIdle,
    onMessage: handleOnMessage,
    crossTab: true,
    syncTimers: 200
  });

  // When continue is clicked (modal closes), reset timer and broadcast to all tabs
  useEffect(() => {
    if (!showTimerModal) {
      reset();
      dispatch(userActions.hasUserLoggedOut(false));
      // Broadcast continue message to all other tabs
      message(CrossTabMessage.CONTINUE, true);
    }
  }, [showTimerModal]);

  // When logout happens, broadcast to all tabs
  useEffect(() => {
    if (hasUserLoggedOut) {
      message(CrossTabMessage.LOGOUT, true);
    }
  }, [hasUserLoggedOut]);

  useEffect(() => {
    reset();
  }, []);

  return null;
};

export default useSessionTimeout;
